"use client";

import { CartService } from "@/services/cart-service";
import { useCartStore } from "@/store/cart-store";
import { Button } from "@/v2-components/ui/button";
import BHapticsLogo from "@/v2-components/core/logo/bhaptics-logo";
import Link from "next/link";
import { NavigationMenuSheet } from "@/v2-components/global-navigation-bar/navigation-menu-sheet";
import type { MenuName } from "@/v2-components/global-navigation-bar/_const/navigation-menus";
import {
  CouponTooltipCaret,
  CouponTooltip,
} from "@/v2-components/global-navigation-bar/profile-coupon-alert";
import { cn } from "@/libs/utils";
import {
  NavigationMenuPanels,
  NavigationMenuProvider,
  NavigationMenuTriggers,
  useNavCorridorBridge,
} from "@/v2-components/global-navigation-bar/navigation-menu";
import { useRouter } from "next/router";
import useUser from "@/api/apis";
import { UserLoginContainer } from "@/components/shared/header/login/user-login-container";
import ShoppingBagIcon from "@/v2-components/core/icon/ShoppingBagIcon";
import { AddedToBagPop } from "@/v2-components/global-navigation-bar/added-to-bag-pop";
import { useAddedToBagStore } from "@/store/cart-store/useAddedToBagStore";
import { useLocale } from "@/store/locale-store";
import { localeDependentProps } from "@/libs/locale/locale-ready";
import GlobeIcon from "@/v2-components/core/icon/globe-icon";
import CountryFlag from "@/components/CountryFlag";
import TruckIcon from "@/v2-components/core/icon/truck-icon";

export default function GlobalNavigationBar({
  className,
  allowCart = true,
  activeMenuOverride,
}: {
  className?: string;
  allowCart?: boolean;
  /**
   * 경로로 활성 1차 메뉴를 못 가르는 화면이 넘긴다 — 지금은 **게임 상세 1곳**
   * (`/games/experiences/{slug}` 가 PC·VR 공용이라 `/games` 접두로는 항상 VR 이 된다).
   * 사유·실측은 `getActiveMenuName` 주석.
   */
  activeMenuOverride?: MenuName;
}) {
  const { items, gameItems, showCart } = useCartStore((c) => ({
    items: c.items,
    showCart: c.showCart,
    gameItems: c.gameItems,
  }));
  const count = CartService.countAllItems({ items, gameItems });
  // 네비 진입 시 담기 팝을 닫는다 (#5366) — 아래 `onPointerEnter` 주석 참조.
  const closeAddedToBag = useAddedToBagStore((s) => s.close);
  const { user } = useUser();

  return (
    <NavigationMenuProvider activeMenuOverride={activeMenuOverride}>
      {/* `relative`: 2deps 공유 시트·패널의 **컨테이닝 블록**. 시트는 `absolute top-full` 로
          이 바 바로 아래에 붙는다.
          ⚠️ 스티키 헤더 래퍼(`PageTemplate` 등)에 맡기면 안 된다 — 그 래퍼에는 `PromoBanner`
          가 GNB **아래**에 같이 들어 있어서 `top:100%` 가 배너 밑으로 내려간다.
          ⚠️ `z-index` 는 주지 않는다. `relative` 만이면 스태킹 컨텍스트가 새로 생기지 않아
          쿠폰 말풍선(`z-40`) vs 시트(`z-[25]`) 승부가 헤더 컨텍스트에서 그대로 유지된다
          (docs/pitfalls.md #31(헤더 밖으로 넘치는 요소) 의 GNB 레이어 스케일). */}
      <div
        className={cn(
          "relative flex items-center justify-center bg-white",
          className,
        )}
      >
        <div
          className={cn(
            "mx-4 flex h-16 w-full max-w-screen-max items-center justify-between transition-opacity duration-200",
            "laptop:mx-5 laptop:h-[70px]",
          )}
        >
          {/* 좌측 묶음 — 햄버거 / 로고 / 1차 메뉴 3개 (Store · PC Games · VR Games). */}
          <div className="flex items-center gap-4">
            {/* Mobile Navigation Menu (Hamburger) */}
            <NavigationMenuSheet />
            <Logo />
            <div
              className="hidden desktop:block"
              onPointerEnter={closeAddedToBag}
            >
              <NavigationMenuTriggers group="left" />
            </div>
          </div>
          {/* ★네비 래퍼의 `onPointerEnter` = 담기 팝 닫기 (#5366). 이게 **돌출 dead zone 의
            해결**이다. 실측(1024 × de): 네비 구간 `x 244..770`, 팝(360px, 아이콘 우측정렬)
            `x 588..948` → **182px 겹친다.** 팝은 헤더 스태킹 컨텍스트 안이라 시트(z-[25])보다
            위에 그려지므로, 그대로 두면 그 구간에서 시트로 내려가는 포인터를 가로챈다
            (docs/pitfalls.md #32(헤더 자손 돌출 hover dead zone)).
            ⚠️ 폭을 줄이거나 정렬을 바꿔서는 못 푼다 — 원 주석의 실측 두 줄 참고.
            ⚠️ **v2 에서 트리거가 좌/우로 갈렸으므로 리스너도 두 래퍼에 각각 있어야 한다.**
               한쪽만 달면 그 반대편 구간에서 회귀한다.
            ⚠️ 세로 패딩을 두지 말 것 — 네비 항목이 이미 `py-6.5` 로 바(h-16/70px)를 꽉
               채우므로 `py-*` 를 더하면 래퍼가 바보다 커져 같은 dead zone 이 생긴다. */}
          {/* relative: 쿠폰 말풍선의 기준 박스. 이 묶음의 우측 끝이 GNB 콘텐츠
            우측 끝이라, 여기에 붙여야 말풍선이 헤더 오른쪽 라인에 맞는다.
            ⚠️ 말풍선 박스를 **왼쪽으로 옮기지 말 것** — 헤더 아래로 돌출하는데, 네비 가로
            구간과 겹치면 드롭다운으로 내려가는 포인터를 가로채 패널이 닫힌다
            (docs/pitfalls.md #32 "헤더 자손이 헤더 아래로 돌출하면…"). 가방을 가리켜야 할
            때는 말풍선이 아니라 **꼬리만** 가방에 앵커한다(아래 CouponTooltipCaret). */}
          {/* 우측 묶음 — 1차 메뉴 3개(Streamer · Developer · Support) + 아이콘.
            ⚠️ 네비를 아이콘 묶음 **안에** 넣지 말 것. 아래 `w-[143px]` 박스는 쿠폰 말풍선과
            담기 팝의 기준 박스이고, 그 폭·우측 끝이 두 요소의 위치를 정한다. 네비를 안에
            넣으면 박스가 넓어져 둘 다 왼쪽으로 밀린다 (#31 · #32). 그래서 형제로 둔다. */}
          <div className="flex items-center gap-4">
            <div
              className="hidden desktop:block"
              onPointerEnter={closeAddedToBag}
            >
              <NavigationMenuTriggers group="right" />
            </div>
            <div className="relative flex h-full w-[143px] items-center justify-end gap-4">
              <Tracking className={cn(!!user && "hidden")} />
              {allowCart && (
                /* relative: 꼬리의 기준 박스 = 가방 아이콘. `left-1/2` 로 중앙에 맞추므로
               "가방까지의 거리" 상수가 필요 없다 — 그 거리는 `Tracking`(로그인 시 숨김)·
               `LocaleButton`(laptop 이상만)이 조건부라 상태·화면폭마다 달라진다.
               flex 필수: `Cart` 는 inline-flex Button 이라 블록 래퍼면 라인박스 leading 이
               붙어 래퍼가 아이콘보다 커지고 꼬리가 그만큼 내려간다. */
                <div className="relative flex">
                  <Cart count={count} onClick={showCart} />
                  <CouponTooltipCaret />
                  {/* 담기 완료 팝 (#5366) — 기준 박스가 **가방 아이콘 래퍼**다.
                  기획: "아이콘 영역 아래로 20px / 우측정렬" → 이 래퍼에 `right-0` 이면
                  팝 우측 끝이 **아이콘 우측 끝**에 맞는다. 우측 묶음에 붙이면 그 오른쪽의
                  아바타·국가 버튼만큼(실측 56px) 더 오른쪽으로 밀린다.
                  ⚠️ 거리 상수를 쓰지 않는 이유는 꼬리와 같다 — `Tracking`(로그인 시 숨김)·
                  `LocaleButton`(laptop 이상만)이 조건부라 그 거리가 상태·화면폭마다 다르다. */}
                  <AddedToBagPop />
                </div>
              )}
              <UserLoginContainer />
              {/* 네비와 같은 축(1280)이다. `laptop:flex` 로 두면 1024~1279 에서 햄버거도
                  네비도 없는데 국가 버튼만 떠 있다. */}
              <div className="hidden items-center desktop:flex">
                <LocaleButton />
              </div>
              <CouponTooltipWithCorridorBridge />
            </div>
          </div>
        </div>

        {/* 공유 시트 + 패널 4개. **바 루트의 absolute 자식**이라 여기 한 번만 렌더한다
          (트리거는 좌/우 두 번 렌더되지만 면은 하나여야 한다). */}
        <NavigationMenuPanels />
      </div>
    </NavigationMenuProvider>
  );
}

/**
 * 쿠폰 말풍선 — **숨기지 않고**, 네비 hover 유지 영역에 참여시킨다.
 *
 * 말풍선은 헤더 아래로 14px 돌출하고 우측 네비와 가로로 74px 겹친다(1280/de 실측).
 * 그 자체는 못 없앤다 — 돌출 0 으로 하려면 top ≤ 109 여야 하는데 가방 아이콘이
 * `90..114` 를 쓰고, 아이콘 아래에 붙는 것이 이 UI 의 설계다(꼬리가 둘을 잇는다).
 *
 * 대신 **닫힘 타이머 취소에 참여**시킨다. 그러면 포인터가 말풍선 위를 지나가도 시트가
 * 살아 있다 — 실측: 이 배선 없이는 `y=143` 에서 닫혔다.
 *
 * ⚠️ 처음엔 「네비 관여 중 말풍선을 숨기는」 쪽으로 만들었다가 되돌렸다. 그건 **쿠폰
 * 알림을 잃는** 대가를 치른다. 이 방식은 둘 다 보인다.
 *
 * ⚠️ `GlobalNavigationBar` 본체에서 훅을 부를 수 없다 — 그 컴포넌트가 Provider 를
 * **렌더하는** 쪽이라 자기 본문은 컨텍스트 밖이다. 그래서 자식으로 분리한다.
 */
const CouponTooltipWithCorridorBridge = () => {
  const bridge = useNavCorridorBridge();
  return <CouponTooltip {...bridge} />;
};

const LocaleButton = () => {
  const { countryCode, setDialog } = useLocale();

  return (
    <button onClick={() => setDialog("select-locale-dialog")}>
      {!countryCode ? (
        <GlobeIcon />
      ) : (
        // 국가 확정 전엔 숨긴다 — 확정 전 값은 URL 로케일 폴백(`/en` → US)이다(src/libs/locale/locale-ready.ts)
        <span
          {...localeDependentProps}
          className="flex items-center gap-1 whitespace-nowrap"
        >
          <div className="flex aspect-square items-center justify-center overflow-hidden rounded-full border border-black">
            <CountryFlag
              className="flex aspect-square scale-x-[1.3]"
              countryCode={countryCode}
            />
          </div>

          <span className="text-xs font-600">{countryCode}</span>
        </span>
      )}
    </button>
  );
};

const Logo = ({ className }: { className?: string }) => {
  const router = useRouter();
  const { languageCode } = useLocale();

  return (
    <button
      onClick={() => {
        if (router.pathname === "/") {
          scrollTo({ behavior: "smooth", top: 0 });
        } else {
          router.push("/", undefined, { locale: languageCode });
        }
      }}
      className={cn(className)}
    >
      <BHapticsLogo className="w-[100px] laptop:w-[133px]" />
    </button>
  );
};

const Tracking = ({ className }: { className?: string }) => {
  const { languageCode } = useLocale();
  return (
    <Link
      locale={languageCode}
      className={cn("p-0 transition-colors hover:text-coolgray-60", className)}
      href="/tracking"
    >
      <TruckIcon />
    </Link>
  );
};

const Cart = ({ count, onClick }: { count: number; onClick: () => void }) => {
  return (
    <Button
      variant="darkgray-text"
      className="relative p-0 hover:text-coolgray-60"
      size="none"
      onClick={onClick}
    >
      <ShoppingBagIcon />
      {count > 0 && (
        <div className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-coolgray-90 text-xs text-white">
          {count}
        </div>
      )}
    </Button>
  );
};
